import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { isNil } from 'lodash'
import { Page } from 'components/Shared/Page'
import { isIdPresent } from 'utils/typeUtils'
import { SectionHeader } from 'components/SectionHeader'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'stores/useSnackbar'
import { formatDateTime } from 'utils/util'
import { BannerDetailsHeader } from './BannerDetailsHeader'
import { useGetSystemBannerDetailsById } from 'hooks/api/useGetSystemBannerDetailsById'
import { EditBannerDetailsModal } from './EditBannerDetailsModal'
import { usePutSystemBanner } from 'hooks/api/usePutSystemBanner'

export const BannerDetailsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModalIsOpen = () => setIsModalOpen(!isModalOpen)
  const id = Number(useParams().id)
  const getBannerQuery = useGetSystemBannerDetailsById({ id })
  const theme = useTheme()
  const queryClient = useQueryClient()
  const setMessage = useSnackbar((state) => state.setMessage)

  const putBannerMutation = usePutSystemBanner({
    id,
    onSuccess: () => {
      setMessage('Banner has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      void queryClient.invalidateQueries({
        queryKey: ['/admin/system-banner/{id}', id],
      })
      toggleModalIsOpen()
    },
    onError: () => {
      setMessage('Error updating Banner', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  if (getBannerQuery.isPending) {
    return <CircularProgress />
  }

  if (getBannerQuery.isError) {
    return <p>An error occurred.</p>
  }

  const banner = getBannerQuery.data

  if (isNil(banner) || !isIdPresent(banner)) {
    return <p>An error occurred.</p>
  }

  return (
    <Page header={<BannerDetailsHeader banner={banner} />}>
      <Box className={'pt-4'}>
        <Box className="pt-4">
          <Box>
            <SectionHeader
              title="Banner Details"
              onClickButton={toggleModalIsOpen}
            />
            <Box
              border={{ md: `1px solid ${theme.palette.grey[200]}` }}
              borderRadius={'5px'}
              p={{ xs: 0, sm: 3 }}
            >
              <Grid
                container
                className="flex flex-row flex-wrap"
                columnGap={15}
              >
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {banner.startDate
                      ? formatDateTime(banner.startDate, 'P p')
                      : 'None'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {banner.endDate
                      ? formatDateTime(banner.endDate, 'P p')
                      : 'None'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Platform Type
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {banner.platformType ?? ''}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                className="flex flex-row flex-wrap"
                columnGap={15}
              >
                <Grid item md={8}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Banner Content
                  </Typography>
                  <Typography height="auto" variant="body1" pb={2}>
                    {banner.content ?? ''}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      <EditBannerDetailsModal
        banner={banner}
        updateIsActive={putBannerMutation.isPending}
        isModalOpen={isModalOpen}
        toggleModalIsOpen={toggleModalIsOpen}
        onSubmitBanner={(banner) => {
          putBannerMutation.mutate(banner)
        }}
      />
    </Page>
  )
}
